// Example: Listen for changes and invalidate queries
import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { getSharedScopedSupabaseClient, sanitizeChannelName, type PostgresChangesEvent } from '@/lib/realtime';
import { markRealtimeSync, subscribeRealtimeHealth } from '@/lib/realtime-health';
import { supabase } from '@/lib/supabase';

type RealtimeInvalidateOptions = {
  enabled?: boolean;
  event?: PostgresChangesEvent;
  filter?: string;
};

export function useRealtimeInvalidate(
  table: string,
  queryKey: any[],
  onInvalidate?: () => void,
  options: RealtimeInvalidateOptions = {},
) {
  const queryClient = useContext(QueryClientContext);
  const queryKeySignature = useMemo(() => JSON.stringify(queryKey), [queryKey]);
  const onInvalidateRef = useRef(onInvalidate);
  const queryKeyRef = useRef(queryKey);
  // Bumped when the connection is presumed dead so the subscription tears down
  // and re-establishes (R27.8 resubscribe-with-backoff intent).
  const [resubscribeEpoch, setResubscribeEpoch] = useState(0);
  const prevAliveRef = useRef(true);

  onInvalidateRef.current = onInvalidate;
  queryKeyRef.current = queryKey;

  // OFF-03: if the realtime connection is presumed dead, refetch immediately
  // and force a resubscribe (R27.8). Only acts on the alive -> dead transition.
  useEffect(() => {
    return subscribeRealtimeHealth((snap) => {
      if (prevAliveRef.current && !snap.alive) {
        queryClient?.invalidateQueries({ queryKey: queryKeyRef.current });
        onInvalidateRef.current?.();
        setResubscribeEpoch((e) => e + 1);
      }
      prevAliveRef.current = snap.alive;
    });
  }, [queryClient]);

  useEffect(() => {
    if (!table || options.enabled === false) return;

    let cancelled = false;
    let channelRef: RealtimeChannel | null = null;
    let debounceTimer: number | null = null;
    const channelName = sanitizeChannelName(
      `public:${table}:${options.filter || 'all'}:${queryKeySignature}`,
    );
    const changesConfig = {
      event: options.event || '*',
      schema: 'public',
      table,
      ...(options.filter ? { filter: options.filter } : {}),
    } as {
      event: PostgresChangesEvent;
      schema: 'public';
      table: string;
      filter?: string;
    };

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const accessToken = data.session?.access_token ?? null;

      // Wire the current session's token in so RLS-honoring events are
      // delivered (a signed-out/guest caller gets an anon channel, as before).
      // The shared per-tab client means every invalidate subscription on a page
      // (menu tables, business, profile) multiplexes over one connection.
      const client: SupabaseClient | null = getSharedScopedSupabaseClient(accessToken);
      if (!client) return;
      if (accessToken) client.realtime.setAuth(accessToken);

      channelRef = client
        .channel(channelName)
        .on('postgres_changes' as never, changesConfig as never, () => {
          markRealtimeSync();
          if (debounceTimer) {
            window.clearTimeout(debounceTimer);
          }
          debounceTimer = window.setTimeout(() => {
            queryClient?.invalidateQueries({ queryKey: queryKeyRef.current });
            onInvalidateRef.current?.();
          }, 250);
        })
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }
      if (channelRef) void channelRef.unsubscribe();
    };
  }, [table, queryKeySignature, queryClient, options.enabled, options.event, options.filter, resubscribeEpoch]);
}
